/**
 * FF#11 section behaviour: countdown gauge, photo slider, card carousel.
 * Web Components so the theme editor can re-render a section without
 * leaving timers running on detached nodes.
 */

(() => {
  // Several sections include this file; only the first copy may run.
  if (window.FFSections) return;
  window.FFSections = true;

const reducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (error) {
    return false;
  }
};

const pad = (n) => String(Math.max(n, 0)).padStart(2, '0');

class FFCountdown extends HTMLElement {
  #timer = null;
  #cycle = [];
  #sweeping = false;

  connectedCallback() {
    this.ends = Date.parse(this.dataset.ends);
    this.starts = Date.parse(this.dataset.starts);
    if (Number.isNaN(this.ends)) return;

    this.cells = this.querySelectorAll('[data-ff-unit]');
    this.bar = this.querySelector('[data-ff-lights]');
    this.segs = [...this.querySelectorAll('[data-ff-seg]')];

    this.#tick();
    this.#timer = setInterval(() => this.#tick(), 1000);
    this.#later(() => this.#sweep(), 320);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    for (const id of this.#cycle) clearTimeout(id);
    this.#cycle = [];
  }

  #later(fn, ms) {
    const id = setTimeout(fn, ms);
    this.#cycle.push(id);
    return id;
  }

  #litFor(now) {
    if (Number.isNaN(this.starts) || this.ends <= this.starts) return null;
    const pct = Math.min(Math.max((now - this.starts) / (this.ends - this.starts), 0), 1);
    this.bar?.setAttribute('aria-valuenow', Math.round(pct * 100));
    return Math.round(pct * this.segs.length);
  }

  #segClass(i, lit) {
    if (i >= lit) return 'ff-lights__seg';
    return i >= lit - 3 ? 'ff-lights__seg ff-lights__seg--hot' : 'ff-lights__seg ff-lights__seg--lit';
  }

  #paintBar(now) {
    const lit = this.#litFor(now);
    if (lit === null || this.#sweeping) return;
    for (const [i, seg] of this.segs.entries()) seg.className = this.#segClass(i, lit);
  }

  #tick() {
    const now = Date.now();
    const left = Math.max(this.ends - now, 0);
    const s = Math.floor(left / 1000);
    const parts = [Math.floor(s / 86400), Math.floor(s / 3600) % 24, Math.floor(s / 60) % 60, s % 60];
    for (const [i, cell] of this.cells.entries()) cell.textContent = pad(parts[i]);
    this.#paintBar(now);
    if (left === 0) clearInterval(this.#timer);
  }

  // Lights run in fast and settle slow, hold, drain from the right, repeat.
  #sweep() {
    const RUN = 1500;
    const HOLD = 1800;
    const DRAIN = 360;
    const lit = this.#litFor(Date.now());
    if (lit === null) return;
    if (reducedMotion()) {
      this.#sweeping = false;
      this.#paintBar(Date.now());
      return;
    }
    this.#sweeping = true;
    if (lit === 0) {
      this.#later(() => this.#sweep(), HOLD);
      return;
    }

    const settle = (t) => t * t;
    for (let k = 0; k < lit; k++) {
      this.#later(() => {
        for (let j = 0; j <= k; j++) this.segs[j].className = this.#segClass(j, k + 1);
        if (k === lit - 1) this.#later(() => this.#drain(lit), HOLD);
      }, Math.round(RUN * settle((k + 1) / lit)));
    }
  }

  #drain(lit) {
    const step = Math.max(8, Math.round(360 / Math.max(lit, 1)));
    for (let k = lit - 1; k >= 0; k--) {
      this.#later(() => {
        this.segs[k].className = 'ff-lights__seg';
        if (k === 0) this.#later(() => this.#sweep(), 200);
      }, (lit - 1 - k) * step);
    }
  }
}

class FFSlider extends HTMLElement {
  #timer = null;
  #index = 0;
  #held = false;
  #userPaused = false;
  #narrow = window.matchMedia('(max-width: 989px)');
  #onBreakpoint = () => this.#layout();

  connectedCallback() {
    this.track = this.querySelector('[data-ff-track]');
    this.slides = this.track ? [...this.track.children] : [];
    this.pips = [...this.querySelectorAll('[data-ff-pip]')];
    this.pauseBtn = this.querySelector('[data-ff-pause]');
    this.count = parseInt(this.dataset.count, 10) || 0;

    for (const [k, pip] of this.pips.entries()) pip.addEventListener('click', () => this.#go(Math.min(k, this.max)));
    this.querySelector('[data-ff-prev]')?.addEventListener('click', () => this.#go(this.#index - 1));
    this.querySelector('[data-ff-next]')?.addEventListener('click', () => this.#go(this.#index + 1));
    this.pauseBtn?.addEventListener('click', () => this.#togglePause());
    // Rotation holds while the pointer or keyboard focus is on the carousel.
    this.addEventListener('mouseenter', () => this.#hold(true));
    this.addEventListener('mouseleave', () => this.#hold(false));
    this.addEventListener('focusin', () => this.#hold(true));
    this.addEventListener('focusout', () => this.#hold(false));
    this.#narrow.addEventListener('change', this.#onBreakpoint);

    this.#layout();
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    this.#narrow.removeEventListener('change', this.#onBreakpoint);
  }

  // One photo per view on phones, three on desktop; re-evaluated whenever
  // the viewport crosses the breakpoint so a resized window or the
  // editor's device toggle never shows three at phone width.
  #layout() {
    this.per = this.#narrow.matches ? 1 : 3;
    this.max = Math.max(this.count - this.per, 0);
    this.#index = Math.min(this.#index, this.max);
    this.#paint();
    this.#restart();
  }

  #paint() {
    this.track.style.transform = `translateX(-${this.#index * (100 / this.per)}%)`;
    for (const [k, pip] of this.pips.entries()) pip.setAttribute('aria-current', k === this.#index ? 'true' : 'false');
    for (const [k, slide] of this.slides.entries()) {
      const visible = k >= this.#index && k < this.#index + this.per;
      slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }
  }

  #go(next) {
    this.#index = (next + this.max + 1) % (this.max + 1);
    this.#paint();
    this.#restart();
  }

  #hold(on) {
    this.#held = on;
    this.#restart();
  }

  #togglePause() {
    this.#userPaused = !this.#userPaused;
    this.pauseBtn.setAttribute('aria-pressed', String(this.#userPaused));
    this.pauseBtn.setAttribute('aria-label', this.#userPaused ? this.pauseBtn.dataset.labelPlay : this.pauseBtn.dataset.labelPause);
    this.#restart();
  }

  #restart() {
    clearInterval(this.#timer);
    const rotating = !reducedMotion() && !this.#userPaused && !this.#held && this.count > this.per;
    this.track.setAttribute('aria-live', rotating ? 'off' : 'polite');
    if (!rotating) return;
    this.#timer = setInterval(() => {
      this.#index = this.#index >= this.max ? 0 : this.#index + 1;
      this.#paint();
    }, 5000);
  }
}

class FFCarousel extends HTMLElement {
  #index = 0;
  #narrow = window.matchMedia('(max-width: 989px)');
  #onResize = () => this.#layout();

  connectedCallback() {
    this.track = this.querySelector('[data-ff-track]');
    this.fill = this.querySelector('[data-ff-fill]');
    this.prev = this.querySelector('[data-ff-prev]');
    this.next = this.querySelector('[data-ff-next]');
    this.count = parseInt(this.dataset.count, 10) || 0;

    this.prev?.addEventListener('click', () => this.#go(this.#index - 1));
    this.next?.addEventListener('click', () => this.#go(this.#index + 1));
    this.track.addEventListener('focusin', (event) => this.#follow(event));
    window.addEventListener('resize', this.#onResize);
    this.#layout();
  }

  // Keyboard users tabbing into an off-screen card get it scrolled into view.
  #follow(event) {
    const card = event.target.closest('[aria-roledescription="slide"]');
    const k = card ? [...this.track.children].indexOf(card) : -1;
    if (k < 0) return;
    if (k < this.#index || k >= this.#index + this.per) this.#go(Math.min(k, this.max));
    const viewport = this.track.parentElement;
    if (viewport) viewport.scrollLeft = 0;
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#onResize);
  }

  #layout() {
    this.per = this.#narrow.matches ? 1 : 3;
    this.max = Math.max(this.count - this.per, 0);
    this.#index = Math.min(this.#index, this.max);
    this.#paint();
  }

  #go(next) {
    this.#index = Math.min(Math.max(next, 0), this.max);
    this.#paint();
  }

  #paint() {
    const card = this.track.children[0];
    const step = card ? card.getBoundingClientRect().width + 18 : 0;
    this.track.style.transform = `translateX(-${this.#index * step}px)`;
    if (this.fill) this.fill.style.width = `${Math.round(((this.#index + this.per) / Math.max(this.count, 1)) * 100)}%`;
    if (this.prev) this.prev.disabled = this.#index === 0;
    if (this.next) this.next.disabled = this.#index >= this.max;
  }
}

class FFSteps extends HTMLElement {
  #timer = null;
  #resume = null;
  #index = 0;
  #narrow = window.matchMedia('(max-width: 989px)');
  #onBreakpoint = () => this.#layout();
  #onScroll = () => this.#sync();
  #onTouch = () => this.#pause();
  #held = false;
  #userPaused = false;

  connectedCallback() {
    this.track = this.querySelector('[data-ff-steps-track]');
    this.pips = [...this.querySelectorAll('[data-ff-steps-pip]')];
    this.pauseBtn = this.querySelector('[data-ff-steps-pause]');
    this.interval = Math.max(parseInt(this.dataset.interval, 10) || 5000, 5000);
    if (!this.track) return;

    for (const [k, pip] of this.pips.entries()) pip.addEventListener('click', () => { this.#go(k); this.#pause(); });
    this.pauseBtn?.addEventListener('click', () => this.#togglePause());
    this.addEventListener('mouseenter', () => this.#hold(true));
    this.addEventListener('mouseleave', () => this.#hold(false));
    this.addEventListener('focusin', () => this.#hold(true));
    this.addEventListener('focusout', () => this.#hold(false));
    this.track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.track.addEventListener('touchstart', this.#onTouch, { passive: true });
    this.track.addEventListener('pointerdown', this.#onTouch, { passive: true });
    this.#narrow.addEventListener('change', this.#onBreakpoint);
    this.#layout();
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    clearTimeout(this.#resume);
    this.track?.removeEventListener('scroll', this.#onScroll);
    this.track?.removeEventListener('touchstart', this.#onTouch);
    this.track?.removeEventListener('pointerdown', this.#onTouch);
    this.#narrow.removeEventListener('change', this.#onBreakpoint);
  }

  // Slides only on phones; desktop keeps the three-up grid.
  #layout() {
    clearInterval(this.#timer);
    // The track scrolls on phones, so keyboard users need to reach it there.
    if (this.#narrow.matches) this.track.setAttribute('tabindex', '0');
    else this.track.removeAttribute('tabindex');
    const rotating = this.#narrow.matches && !reducedMotion() && !this.#userPaused && !this.#held;
    this.track.setAttribute('aria-live', rotating ? 'off' : 'polite');
    if (!rotating) return;
    this.#timer = setInterval(() => this.#go(this.#index + 1), this.interval);
  }

  #hold(on) {
    this.#held = on;
    this.#layout();
  }

  #togglePause() {
    this.#userPaused = !this.#userPaused;
    this.pauseBtn.setAttribute('aria-pressed', String(this.#userPaused));
    this.pauseBtn.setAttribute('aria-label', this.#userPaused ? this.pauseBtn.dataset.labelPlay : this.pauseBtn.dataset.labelPause);
    clearTimeout(this.#resume);
    this.#layout();
  }

  #step() {
    const card = this.track.children[0];
    if (!card) return 0;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  #go(next) {
    const count = this.track.children.length;
    if (!count) return;
    this.#index = ((next % count) + count) % count;
    this.track.scrollTo({ left: this.#index * this.#step(), behavior: 'smooth' });
  }

  #sync() {
    const step = this.#step();
    if (!step) return;
    this.#index = Math.round(this.track.scrollLeft / step);
    for (const [k, pip] of this.pips.entries()) pip.setAttribute('aria-current', k === this.#index ? 'true' : 'false');
  }

  // A swipe pauses the auto-advance and hands control back after a beat.
  #pause() {
    clearInterval(this.#timer);
    clearTimeout(this.#resume);
    this.#resume = setTimeout(() => this.#layout(), this.interval + 2000);
  }
}

/* Gallery lightbox. Buttons marked data-ff-lb-open carry the full-size src and alt. */
class FFLightbox extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-ff-lb-open]'));
    this.dialog = this.querySelector('[data-ff-lb]');
    if (!this.dialog || !this.items.length) return;
    this.img = this.dialog.querySelector('[data-ff-lb-img]');
    this.caption = this.dialog.querySelector('[data-ff-lb-caption]');
    this.count = this.dialog.querySelector('[data-ff-lb-count]');
    this.closeBtn = this.dialog.querySelector('[data-ff-lb-close]');
    this.index = -1;
    this.onClick = (event) => this.#handleClick(event);
    this.onKey = (event) => this.#handleKey(event);
    this.onClose = () => this.#afterClose();
    this.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKey);
    this.dialog.addEventListener('close', this.onClose);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('keydown', this.onKey);
    this.dialog?.removeEventListener('close', this.onClose);
    if (this.dialog?.open) this.dialog.close();
  }

  #handleClick(event) {
    const opener = event.target.closest('[data-ff-lb-open]');
    if (opener) return this.open(this.items.indexOf(opener));
    if (event.target.closest('[data-ff-lb-prev]')) return this.step(-1);
    if (event.target.closest('[data-ff-lb-next]')) return this.step(1);
    if (event.target.closest('[data-ff-lb-close]') || event.target === this.dialog) this.close();
  }

  #handleKey(event) {
    if (this.index < 0) return;
    if (event.key === 'ArrowLeft') this.step(-1);
    if (event.key === 'ArrowRight') this.step(1);
  }

  open(index) {
    this.index = index;
    this.#render();
    if (!this.dialog.open) this.dialog.showModal();
    document.body.style.overflow = 'hidden';
    this.closeBtn.focus();
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  /* Runs for our close() and for the native Escape key alike. */
  #afterClose() {
    const opener = this.items[this.index];
    this.index = -1;
    document.body.style.overflow = '';
    if (opener) opener.focus();
  }

  step(delta) {
    const total = this.items.length;
    this.index = (this.index + delta + total) % total;
    this.#render();
  }

  #render() {
    const item = this.items[this.index];
    this.img.src = item.dataset.ffLbSrc;
    this.img.alt = item.dataset.ffLbAlt || '';
    this.caption.textContent = item.dataset.ffLbAlt || '';
    const template = this.dataset.counter || '[n] / [total]';
    this.count.textContent = template.replace('[n]', this.index + 1).replace('[total]', this.items.length);
  }
}

/* Marquee: the pause button is the WCAG stop control; hover and focus also hold it (CSS). */
class FFMarquee extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-ff-pause]');
    this.onClick = () => this.#toggle();
    this.button?.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  #toggle() {
    const paused = this.classList.toggle('ff-marquee--paused');
    this.button.setAttribute('aria-pressed', String(paused));
    this.button.setAttribute('aria-label', paused ? this.button.dataset.labelPlay : this.button.dataset.labelPause);
  }
}

if (!customElements.get('ff-countdown')) customElements.define('ff-countdown', FFCountdown);
if (!customElements.get('ff-slider')) customElements.define('ff-slider', FFSlider);
if (!customElements.get('ff-carousel')) customElements.define('ff-carousel', FFCarousel);
if (!customElements.get('ff-steps')) customElements.define('ff-steps', FFSteps);
if (!customElements.get('ff-lightbox')) customElements.define('ff-lightbox', FFLightbox);
if (!customElements.get('ff-marquee')) customElements.define('ff-marquee', FFMarquee);

/** Shipping estimate: business days from today, skipping weekends. */
(() => {
  const fmt = { month: 'long', day: 'numeric' };
  const businessDay = (n) => {
    const d = new Date();
    let count = 0;
    while (count < n) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) count += 1;
    }
    return d.toLocaleDateString(document.documentElement.lang || 'en-US', fmt);
  };
  for (const el of document.querySelectorAll('[data-ff-ship]')) {
    const min = parseInt(el.dataset.min, 10) || 5;
    const max = parseInt(el.dataset.max, 10) || 7;
    el.querySelector('[data-ff-ship-from]').textContent = businessDay(min);
    el.querySelector('[data-ff-ship-to]').textContent = businessDay(max);
  }
})();

})();
